import { FieldErrors, FieldValues, Path, UseFormRegister } from "react-hook-form";
import { Input } from "./Input";
import { useTranslation } from "react-i18next";
import { emailRegex } from "../validation";
import { isEmptyString, isNotEmptyString } from "../common";

export const UserInputs = <TFIeldValues extends FieldValues, TProperty extends Path<TFIeldValues>>({
	register,
	errors,
	lastNameProp,
	firstNameProp,
	emailProp,
	emailConfirmationProp,
	isUniqueEmail,
	additionalValidate
}: {
	register: UseFormRegister<TFIeldValues>;
	errors: FieldErrors<TFIeldValues>;
	lastNameProp: TProperty;
	firstNameProp: TProperty;
	emailProp: TProperty;
	emailConfirmationProp: TProperty;
	isUniqueEmail: (propName: TProperty, values: TFIeldValues) => boolean;
	additionalValidate?: (propName: TProperty, values: TFIeldValues) => string | undefined;
}) => {
	const { t } = useTranslation();

	const isRequired = (values: TFIeldValues): boolean => {
		return (
			isNotEmptyString(values[lastNameProp]) ||
			isNotEmptyString(values[firstNameProp]) ||
			isNotEmptyString(values[emailProp]) ||
			isNotEmptyString(values[emailConfirmationProp])
		);
	};

	const isRequiredAndEmpty = (value: string, values: TFIeldValues): boolean => {
		return isRequired(values) && isEmptyString(value);
	};

	return (
		<div className="row space-s-t">
			<div className="col-md-3 col-sm-12 col-xs-12">
				<Input
					register={register}
					name={lastNameProp}
					errors={errors}
					label="lastName"
					placeholder="lastName"
					registerOptions={{
						validate: {
							requiredIfAnyNonEmpty: (v, values) => (isRequiredAndEmpty(v, values) ? t("requiredField") : undefined),
							...(additionalValidate && {
								additionalValidate: additionalValidate
							})
						}
					}}
				/>
			</div>
			<div className="col-md-3 col-sm-12 col-xs-12">
				<Input
					register={register}
					name={firstNameProp}
					errors={errors}
					label="firstName"
					placeholder="firstName"
					registerOptions={{
						validate: {
							requiredIfAnyNonEmpty: (v, values) => (isRequiredAndEmpty(v, values) ? t("requiredField") : undefined)
						}
					}}
				/>
			</div>
			<div className="col-md-3 col-sm-12 col-xs-12">
				<Input
					register={register}
					name={emailProp}
					errors={errors}
					label="email"
					placeholder="email"
					inputType="email"
					registerOptions={{
						deps: [emailConfirmationProp],
						validate: {
							requiredIfAnyNonEmpty: (v, values) => (isRequiredAndEmpty(v, values) ? t("requiredField") : undefined),
							emailFormat: (v, values) => (!isRequired(values) || emailRegex.test(v) ? undefined : t("invalidEmail")),
							uniqueEmail: (_, values) =>
								!isRequired(values) || isUniqueEmail(emailProp, values) ? undefined : t("notUniqueEmail")
						}
					}}
				/>
			</div>
			<div className="col-md-3 col-sm-12 col-xs-12">
				<Input
					register={register}
					name={emailConfirmationProp}
					errors={errors}
					label="emailRepeat"
					placeholder="emailRepeat"
					inputType="email"
					registerOptions={{
						validate: {
							requiredIfAnyNonEmpty: (v, values) => (isRequiredAndEmpty(v, values) ? t("requiredField") : undefined),
							emailFormat: (v, values) => (!isRequired(values) || emailRegex.test(v) ? undefined : t("invalidEmail")),
							sameEmail: (_, values) =>
								isRequired(values) && values[emailProp] !== values[emailConfirmationProp]
									? t("emailsDontMatch")
									: undefined
						}
					}}
				/>
			</div>
		</div>
	);
};
